// Secondary navigation for the Accounting module. Each entry controls a real
// route (see AccountingLayout.jsx) - nothing here toggles local state or CSS
// visibility, per Phase 2 rules. Paths are absolute (rooted at
// ACCOUNTING_BASE) so every Link/NavLink resolves the same way regardless of
// how deep the component rendering it happens to sit in the route tree.
//
// Structure: a compact top bar - "Dashboard | Masters v | Transactions v |
// Members v | Vendors v | Banking v | Reports v | Administration v" - each
// non-Dashboard entry is a dropdown group (AccountingShell.jsx renders these
// with the existing shadcn DropdownMenu component). This intentionally
// replaces the earlier flat "every page is a button" layout.
export const ACCOUNTING_BASE = "/client/society/accounting";

const accountingNav = [
  { type: "link", label: "Dashboard", to: `${ACCOUNTING_BASE}/dashboard` },
  {
    type: "dropdown",
    group: "Masters",
    items: [
      { label: "Chart of Accounts", to: `${ACCOUNTING_BASE}/chart-of-accounts` },
      { label: "Ledgers", to: `${ACCOUNTING_BASE}/ledgers` },
      { label: "Vendors", to: `${ACCOUNTING_BASE}/vendors` },
      { label: "Bank Accounts", to: `${ACCOUNTING_BASE}/bank-accounts` },
      { label: "Funds", to: `${ACCOUNTING_BASE}/funds` },
      { label: "Fixed Deposits", to: `${ACCOUNTING_BASE}/fixed-deposits` },
    ],
  },
  {
    type: "dropdown",
    group: "Transactions",
    items: [
      { label: "All Vouchers", to: `${ACCOUNTING_BASE}/vouchers` },
      { label: "Receipt", to: `${ACCOUNTING_BASE}/receipts` },
      { label: "Payment", to: `${ACCOUNTING_BASE}/payments` },
      { label: "Contra", to: `${ACCOUNTING_BASE}/contra` },
      { label: "Journal", to: `${ACCOUNTING_BASE}/journals` },
      { label: "Purchase / Vendor Bill", to: `${ACCOUNTING_BASE}/vendor-bills` },
      { label: "Credit Note", to: `${ACCOUNTING_BASE}/credit-notes` },
      { label: "Debit Note", to: `${ACCOUNTING_BASE}/debit-notes` },
    ],
  },
  {
    type: "dropdown",
    group: "Members",
    items: [
      { label: "Member Outstanding / Receivables", to: `${ACCOUNTING_BASE}/member-receivables` },
      { label: "Quick Member Receipts", to: `${ACCOUNTING_BASE}/quick-member-receipts` },
      { label: "Member Advances", to: `${ACCOUNTING_BASE}/member-advances` },
      { label: "Member Ledger", to: `${ACCOUNTING_BASE}/member-ledger` },
    ],
  },
  {
    type: "dropdown",
    group: "Vendors",
    items: [
      { label: "Vendor Outstanding / Payables", to: `${ACCOUNTING_BASE}/vendor-payables` },
      // Phase 15 (Vendor Bills & Payables) items 5 + 9: per-bill Outstanding
      // list (Bill No./Bill Date/Due Date/Original/Paid/Balance/Age) with
      // Vendor/Bill No./Date/Status filters and View/Edit/Print/Delete
      // actions - distinct from the per-vendor summary above.
      { label: "Vendor Bills Outstanding", to: `${ACCOUNTING_BASE}/reports/vendor-bills` },
      { label: "Vendor Ledger", to: `${ACCOUNTING_BASE}/vendor-ledger` },
    ],
  },
  {
    type: "dropdown",
    group: "Banking",
    items: [
      // Same route as Reports -> Bank Book on purpose: this is the one
      // genuine case the spec asks to expose from two menus (operational
      // banking vs. the reports catalogue), unlike e.g. Bank Accounts which
      // must not be duplicated.
      { label: "Bank Book", to: `${ACCOUNTING_BASE}/reports/bank-book` },
      // Phase 16: this now opens the new manual per-voucher reconcile page
      // (BankReconciliationManualPage.jsx) - no bank-statement import
      // needed, matches the spec exactly. The earlier Phase 9 statement-
      // import/auto-match workspace still exists, completely unmodified,
      // one line below - demoted the same way the original Phase 1/2 CSV
      // page was demoted when Phase 9 replaced it.
      // PHASE 19: both reconciliation entry points require the "Reconcile
      // Bank" permission (bank.reconcile) - see accountingNav's consumer in
      // AccountingShell.jsx.
      { label: "Bank Reconciliation", to: `${ACCOUNTING_BASE}/bank-reconciliation`, permission: "bank.reconcile" },
      { label: "Statement Import & Auto-Match", to: `${ACCOUNTING_BASE}/bank-reconciliation-statement-import`, permission: "bank.reconcile" },
    ],
  },
  {
    type: "dropdown",
    group: "Reports",
    items: [
      { label: "Monthly Society Balance Sheet", to: `${ACCOUNTING_BASE}/reports/monthly-committee-accounts` },
      { label: "Trial Balance", to: `${ACCOUNTING_BASE}/reports/trial-balance` },
      { label: "General Ledger", to: `${ACCOUNTING_BASE}/reports/general-ledger` },
      { label: "Day Book", to: `${ACCOUNTING_BASE}/reports/day-book` },
      { label: "Cash Book", to: `${ACCOUNTING_BASE}/reports/cash-book` },
      { label: "Bank Book", to: `${ACCOUNTING_BASE}/reports/bank-book` },
      { label: "Income & Expenditure", to: `${ACCOUNTING_BASE}/reports/income-expenditure` },
      { label: "Balance Sheet", to: `${ACCOUNTING_BASE}/reports/balance-sheet` },
      { label: "Member Receivables Report", to: `${ACCOUNTING_BASE}/reports/receivables` },
      { label: "Vendor Payables Report", to: `${ACCOUNTING_BASE}/reports/payables` },
      { label: "Fund Position", to: `${ACCOUNTING_BASE}/reports/fund-position` },
    ],
  },
  {
    type: "dropdown",
    group: "Administration",
    items: [
      { label: "Financial Years", to: `${ACCOUNTING_BASE}/financial-years` },
      // PHASE 19: each Administration item now declares the permission it
      // actually requires (see AccountingShell.jsx, which hides an item a
      // user lacks) - Close/Reopen Financial Year, and the two "Manage
      // Settings" pages, rather than every Administration item being shown
      // to anyone who can merely reach the Accounting module.
      { label: "Financial Year Closing", to: `${ACCOUNTING_BASE}/financial-year-closing`, permission: "accounting.fyclose" },
      { label: "Voucher Numbering", to: `${ACCOUNTING_BASE}/settings/voucher-numbering`, permission: "accounting.settings" },
      { label: "Billing Ledger Mapping", to: `${ACCOUNTING_BASE}/settings/billing-mapping`, permission: "accounting.settings" },
      // "Opening Balances" (the old multi-tab migration/draft/confirm
      // workflow) is deliberately removed from this menu and its route now
      // redirects away (Phase 10/11) - opening balances are entered directly
      // on each Bank Account/Ledger/Vendor/Fund/Fixed Deposit's own form and
      // on each member's own row in Member Receivables, with no separate
      // screen, draft, or confirmation step. Backend records/history
      // endpoints are left intact so any already-posted historical batch
      // data remains valid; only the entry point for NEW data was removed.
      //
      // "Go-Live Settings" (formerly labelled "Migration") is a SEPARATE,
      // still-needed feature - just a Financial Year / Go-Live Date /
      // Previous Software note gating when Billing stops auto-posting
      // historical bills into Accounting. It is not part of the retired
      // opening-balance workflow, so it is restored here (Phase 11),
      // renamed to avoid the retired "Migration" terminology.
      { label: "Go-Live Settings", to: `${ACCOUNTING_BASE}/migration`, permission: "accounting.settings" },
      { label: "Audit Trail", to: `${ACCOUNTING_BASE}/audit-trail`, permission: "audit.view" },
      // Auditor/CA Access management is deliberately admin/client-only on
      // the backend (creating external login credentials is a step above
      // ordinary "Manage Settings") - gated here the same way rather than
      // introducing a tenth permission just for this one page.
      { label: "Auditor / CA Access", to: `${ACCOUNTING_BASE}/auditors`, permission: "__owner_only__" },
      { label: "Accounting Settings", to: `${ACCOUNTING_BASE}/settings`, permission: "accounting.settings" },
    ],
  },
];

export default accountingNav;
